import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";
import {GiustificativoRecord, GruppoEventi, IdTabellaGenericaAzienda} from "@commons/mapping/GiustificativoRecord";
import {UseFieldArrayReturn} from "react-hook-form/dist/types";
import React, {useCallback, useEffect} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {CausaleWFRecord} from "@commons/mapping/CausaleWFRecord";
import classNames from "classnames";
import styles from "./EditGiustificativi.module.css";
import {ComboBoxGiustificativo} from "@components/comboBox/ComboBoxGiustificativo";
import {GiustificativoFilter} from "../GiustificativoFilter";
import InputEx from "@components/wrapperComponents/InputEx";
import RangeHH from "../dashboardWF/RangeHH";
import ComboBoxCdiC from "@components/comboBox/ComboBoxCdiC";
import TooltipEx from "@components/Tooltip";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {DateTime} from "luxon";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {Giustificativi, TipoGiu} from "@commons/mapping/Giustificativi";
import {IconFirstAidKit, IconMinus, IconPlus} from "@tabler/icons-react";
import {Calc} from "@commons/mapping/Calc";
import {InputCleave2} from "@components/wrapperComponents/InputCleave2";
import {GiuFormValues, PresenzeGiustificativoEx} from "../localMapping/PresenzeGiustificativoEx";

interface EditGiustificativiProps {
    lavoratore: Pick<LavoratorePresenzeRecord, 'causaliWF' | 'status'>,
    day: PresenzeGiornataPlusRecord,
    giustificativi: GiustificativoRecord[],
    gruppiEventi?: GruppoEventi,
    name: "giustificativiHHGiornata" | "giustificativiQIGiornata",
    useFieldArrayReturn: UseFieldArrayReturn<GiuFormValues, "giustificativiHHGiornata" | "giustificativiQIGiornata", "id">,
    showRange?: boolean,
    showLabel?: boolean,
    labelClassName?: string,
    giuClassName?: string,
    quantitaOrarieIn?: 'CENTESIMI' | 'MINUTI',
    onChange?: (causali: PresenzeGiustificativoEx[]) => void,
}

export const EditGiustificativi: React.FC<EditGiustificativiProps> = ({
                                                                          lavoratore, day,
                                                                          giustificativi, gruppiEventi,
                                                                          name, useFieldArrayReturn,
                                                                          showRange = true, showLabel = true, labelClassName = "",
                                                                          giuClassName = '',
                                                                          quantitaOrarieIn = 'CENTESIMI',
                                                                          onChange,
                                                                      }) => {
    const {
        watch,
        getValues,
        setValue,
        control,
        register,
        trigger,
        formState: {errors,}
    } = useFormContext<GiuFormValues>();
    const {fields, append, remove} = useFieldArrayReturn;
    const defaultValue = null;

    // Metodo per "pulire" il primo rigo dei giustificativi giornalieri
    const clearFirstRow = () => {
        setValue(`${name}.0.idWF`, 0);
        setValue(`${name}.0.idAttestato`, '');
        setValue(`${name}.0.giustificativo`, null);
        setValue(`${name}.0.codGiu`, '');
        setValue(`${name}.0.quantita`, 0);
        setValue(`${name}.0.quantitaHHMM`, '');
        setValue(`${name}.0.oraInizio`, '');
        setValue(`${name}.0.oraFine`, '');
        setValue(`${name}.0.valoreEvento`, '');
    }

    // Come isQuantitaValida di FPNet-GestionePlus.js
    const isQuantitaValida = function (quantita: number, giustificativo: GiustificativoRecord | null, quantitaOrarieIn: 'CENTESIMI' | 'MINUTI' = 'CENTESIMI') {
        if (giustificativo) {
            if (GiustificativoRecord.hasRange(giustificativo)) {
                if (day !== null) {
                    if (GiustificativoRecord.isOrario(giustificativo)) {
                        if (quantitaOrarieIn === 'CENTESIMI') {
                            quantita = Math.round(quantita * 60);
                        } else {
                            quantita = Calc.hhmm2hhcc(quantita);
                            quantita = Math.round((quantita / 100) * 60);
                        }
                    }
                    if (GiustificativoRecord.isRangeValid(giustificativo, quantita, RiepilogoOrePlus.getTotaleHPrev(day.totali), false)) {
                        return true;
                    }
                } else {
                    return true;
                }
            } else {
                return true;
            }
        } else {// senza giustificativo la quantità non è editabile, devo ritenere che si tratta della validazione di un "rigo" di giustificazione vuoto!!!
            return true;
        }
        return false;
    };

    const onChangeRange = (index: number, _inizio: number, _fine: number, durata: number) => {
        // console.log(inizio, fine, durata);
        if (quantitaOrarieIn === 'MINUTI') {
            const p = Calc.getParts(durata || 0)!;
            p.frazione /= 60;
            const quantita = p.ore + p.frazione;
            setValue(`giustificativiHHGiornata.${index}.quantita`, quantita);
        }
        if (onChange) onChange(giustificativiGiornata);
    };

    const onNotANumber = (index: number, _inizio: number, _fine: number, _durata: number) => {
        setValue(`giustificativiHHGiornata.${index}.quantita`, 0);
        if (onChange) onChange(giustificativiGiornata);
    };

    // watch per array giustificativi
    // la versione watch('giustificativiHHGiornata') NON va usata per gli array!!!
    const giustificativiGiornata = useWatch({
        name: name!,
        control
    });

    const setupQuantita = useCallback((giustificativo: GiustificativoRecord | undefined, index: number) => {
        if (giustificativo && GiustificativoRecord.hasFixedVal(giustificativo)) {
            setValue(`${name}.${index}.quantita`, giustificativo.valMin);
        }
    }, [name, setValue]);

    // Effect per l'allineamento della edit del codice giustificativo con la combobox corrispondente
    useEffect(() => {
        if (giustificativiGiornata) {
            giustificativiGiornata.forEach((rigo, index) => {
                const {codGiu, giustificativo: giustificativoCombo} = rigo;

                if (codGiu && (!giustificativoCombo || giustificativoCombo.codGiu!.toUpperCase() !== codGiu.toUpperCase())) {
                    console.log("Allineo edit codice giustificativo con valore da combo");
                    const giuDaSelezionare = giustificativi.find(v => v.codGiu!.toUpperCase() === codGiu.toUpperCase());
                    if (giuDaSelezionare) {
                        setValue(`${name}.${index}.giustificativo`, giuDaSelezionare);
                        setValue(`${name}.${index}.tipoGiu`, Giustificativi.getTipoGiustificativo(giuDaSelezionare));
                        setupQuantita(giuDaSelezionare, index);
                    } else if (giustificativiGiornata[index].giustificativo) // sembra superfluo controllare che non sia nullo, ma senza tale controllo tutto l'effetto va in loop!
                    {
                        setValue(`${name}.${index}.giustificativo`, null);
                    }

                    if (onChange) onChange(giustificativiGiornata);

                } else if (!codGiu && giustificativoCombo) {
                    console.log("reset combo");
                    setValue(`${name}.${index}.idWF`, 0);
                    setValue(`${name}.${index}.idAttestato`, '');
                    setValue(`${name}.${index}.giustificativo`, null);
                    setValue(`${name}.${index}.quantita`, 0);
                    setValue(`${name}.${index}.quantitaHHMM`, '');

                    if (onChange) onChange(giustificativiGiornata);
                }

            });
        }
    }, [name, giustificativi, setValue, giustificativiGiornata, onChange, setupQuantita]);

    const onChangeComboBox = (v: GiustificativoRecord | null, index: number) => {
        if (v) {
            setValue(`${name}.${index}.codGiu`, v.codGiu!);
            setValue(`${name}.${index}.tipoGiu`, Giustificativi.getTipoGiustificativo(v));
            setupQuantita(v, index);
        } else {
            setValue(`${name}.${index}.idWF`, 0);
            setValue(`${name}.${index}.idAttestato`, '');
            setValue(`${name}.${index}.codGiu`, '');
            setValue(`${name}.${index}.quantita`, 0);
            setValue(`${name}.${index}.quantitaHHMM`, '');
        }
        if (onChange) onChange(giustificativiGiornata);
    }

    return <>{fields.map((field, index) => {
        const isLast = index === fields.length - 1;
        const showAdd = fields.length < PresenzeGiustificativo.maxNumGiustificativi;

        const idWF = watch(`${name}.${index}.idWF`);
        const idAttestato = watch(`${name}.${index}.idAttestato`);
        const richiestaWF = lavoratore.causaliWF ? lavoratore.causaliWF.find(r => r.wf.idWF === idWF && r.giornoDt === day.dtGiornata) : undefined;
        const permettiModificaGiustificativo = (!idAttestato && !idWF) || CausaleWFRecord.permettiModificaGiustificativo(richiestaWF?.causaleWF || null);
        const permettiModificaQuantita = !idWF || CausaleWFRecord.permettiModificaQuantita(richiestaWF?.causaleWF || null);

        const giustificativo: GiustificativoRecord | null = watch(`${name}.${index}.giustificativo`);
        const idTabella: IdTabellaGenericaAzienda | null = (giustificativo && giustificativo.specificaValore) ? giustificativo.idGruppoValori : null;
        const codGiu = watch(`${name}.${index}.codGiu`)

        const btnPlus = <button type="button" className={classNames(styles.plusBtnS, !(isLast && showAdd) && styles.hiddenBtn)}
                                onClick={() => {
                                    append(PresenzeGiustificativoEx.factory());
                                }}>
            <IconPlus className="h-5 w-5"/>
        </button>;

        const btnMinus = <button type="button" className={classNames(styles.minusBtnS, !(isLast && showAdd) && styles.hiddenBtn)}
                                 onClick={() => {
                                     if (fields.length > 1)
                                         remove(index);
                                     else
                                         clearFirstRow();
                                     if (onChange) onChange(giustificativiGiornata);
                                 }}>
            <IconMinus className="h-5 w-5"/>
        </button>;

        //console.log('field.id: ', field.id);
        const giuNonCompilato = !giustificativiGiornata[index] || !giustificativiGiornata[index].giustificativo || !giustificativiGiornata[index].codGiu;
        const giuConQuantitaPredefinita = !!(giustificativiGiornata[index]
            && giustificativiGiornata[index].giustificativo
            && GiustificativoRecord.hasFixedVal(giustificativiGiornata[index].giustificativo!));
        const tipoGiu: TipoGiu | TipoGiu[] = name === 'giustificativiHHGiornata' ? 'O' : ['Q', 'I', 'E'];
        const labelTipoGiu = name === 'giustificativiHHGiornata' ? 'Giustificativi ad ore' : 'Giustificativi a quantità/importo';

        /*
                console.log('giuNonCompilato: ', name, index, giuNonCompilato);
                console.log('giustificativiGiornata index: ', name, index, giustificativiGiornata[index]);
                console.log('giustificativiGiornata giust: ', name, index, !giustificativiGiornata[index].giustificativo);
                console.log('giustificativiGiornata codGiu: ', name, index, !giustificativiGiornata[index].codGiu);
                console.log(index,field.id);
        */

        const quantita = <InputCleave2<GiuFormValues>
            options={{
                allowNegative: true, allowedDecimalSeparators: [','], fixedDecimalScale: false, decimalScale: 3,
                thousandsGroupStyle: "thousand", thousandSeparator: "'"
            }}
            valueAsNumber={true}
            rules={{
                validate: {
                    validate: v => giuNonCompilato || isQuantitaValida(v as number, giustificativo, quantitaOrarieIn) || GiustificativoRecord.getRangeText2021(giustificativo!),
                    maxLength: v => v <= 99999.999 || 'Valore massimo 99999.999!'
                }
            }}
            label={index === 0 || showLabel ? "Quantità/imp." : undefined}
            labelClassName={labelClassName}
            name={`${name}.${index}.quantita`}
            disabled={giuNonCompilato || giuConQuantitaPredefinita}
            control={control}
            onChange={() => {
                if (onChange) onChange(giustificativiGiornata);
            }}
        />
        /*errorPortalId={errorPortalId}*/
        const optionalFields = !showRange || giuNonCompilato || !!(giustificativiGiornata[index] && giustificativiGiornata[index].giustificativo && giustificativiGiornata[index].giustificativo!.tipoOreGiu === 'Z');

        return <section key={field.id}>
            <div className="mb-3">
                <div className={`flex flex-row items-start ${giuClassName}`}>
                    <div className="mx-2 basis-4/12 lg:basis-5/12">
                        <ComboBoxGiustificativo<GiuFormValues> label={labelTipoGiu}
                                                               showLabel={index === 0 || showLabel} labelClassName={labelClassName}
                                                               control={control} name={`${name}.${index}.giustificativo`}
                                                               defaultValue={defaultValue}
                                                               data={giustificativi
                                                                   .filter((_giu) => true) // todo verificare l'eventuale presenza di un filtro legato all'utente!!!
                                                                   .filter(giu => GiustificativoFilter.filterTipoGiu(giu, tipoGiu))
                                                                   .filter(giu => GiustificativoFilter.filterHasStatus(giu, LavoratorePresenzeRecord.getStatus(lavoratore, day.dtGiornata)))}
                                                               isLoading={false}
                                                               valueAsObject={true}
                                                               onChange={(v) => onChangeComboBox(v, index)}
                                                               addOnBefore={(idWF || idAttestato) ? <>
                                                                   {idWF && <WFIcon richiestaWF={richiestaWF} idWF={idWF}/>}
                                                                   {idAttestato && <MalattiaIcon idAttestato={idAttestato}/>}
                                                               </> : undefined}
                                                               disabled={!permettiModificaGiustificativo}
                                                               rules={{required: !!codGiu}}
                        />
                    </div>
                    <div className="mx-2 basis-1/12">
                        <InputEx
                            inputClass="uppercase"
                            id={`${name}.${index}.codGiu`}
                            label={index === 0 || showLabel ? "Codice" : undefined}
                            labelClassName={labelClassName}
                            type="text"
                            name={`${name}.${index}.codGiu`}
                            register={register}
                            disabled={!permettiModificaGiustificativo}
                        />
                    </div>
                    {name === 'giustificativiHHGiornata' && <RangeHH<GiuFormValues> control={control}
                                                                                    trigger={trigger}
                                                                                    getValues={getValues}
                                                                                    setValue={setValue}
                                                                                    optionalFields={optionalFields}
                                                                                    nameInizio={`${name}.${index}.oraInizio`}
                                                                                    nameFine={`${name}.${index}.oraFine`}
                                                                                    nameQuantita={`${name}.${index}.${quantitaOrarieIn === 'MINUTI' ? 'quantitaHHMM' : 'quantita'}`}
                                                                                    quantitaOrarieIn={quantitaOrarieIn}
                                                                                    validate={v => giuNonCompilato || isQuantitaValida(v as number, giustificativo, quantitaOrarieIn) || GiustificativoRecord.getRangeText2021(giustificativo!)}
                                                                                    errorPortalId={`errorQuantita${index}`}
                                                                                    onChangeRange={(inizio: number, fine: number, durata: number) => onChangeRange(index, inizio, fine, durata)}
                                                                                    onNotANumber={(inizio: number, fine: number, durata: number) => onNotANumber(index, inizio, fine, durata)}
                                                                                    showLabel={index === 0 || showLabel} labelsClassName={labelClassName}
                                                                                    disableFields={giuNonCompilato || !permettiModificaQuantita}
                    >
                        {(hhInizio, hhFine, quantita) => {
                            return <>
                                {showRange && <>
                                    <div className="mx-2 basis-2/12 lg:basis-1/12">
                                        {hhInizio}
                                    </div>
                                    <div className="mx-2 basis-2/12 lg:basis-1/12">
                                        {hhFine}
                                    </div>
                                </>}
                                <div className="mx-2 basis-1/12">
                                    {quantita}
                                </div>
                            </>
                        }
                        }
                    </RangeHH>}
                    {name === 'giustificativiQIGiornata' && <div className="mx-2 basis-1/12">
                        {quantita}
                    </div>
                    }
                    <div className="mx-2 basis-1/12 self-end">
                        {btnPlus}
                        {btnMinus}
                    </div>
                </div>

                {/* SPECIFICA GIUSTIFICATIVI AD EVENTO */}
                {giustificativo?.specificaValore && <div className="flex flex-row mt-1 justify-end">
                    <div className="mx-2 basis-4/12 lg:basis-5/12">
                        <ComboBoxCdiC<GiuFormValues> label="Specifica:" control={control} name={`${name}.${index}.valoreEvento`}
                                                     defaultValue={null}
                                                     data={idTabella ? gruppiEventi![idTabella!] : []}/>
                    </div>
                </div>}

                <div className="flex flex-row">
                    <div className="basis-4/6"/>
                    <div id={`errorQuantita${index}`} className="basis-2/6"></div>
                </div>
            </div>
        </section>
    })}</>;
}

export const WFIcon: React.FC<{
    richiestaWF: PresenzeCausaleWFRecord | undefined,
    idWF: number
}> = ({richiestaWF, idWF}) => {
    return <div className="bg-gray-200 mt-1 p-1 border-l rounded-l w-[38px] h-[38px]">
        <TooltipEx tooltip={getWFTooltip(richiestaWF, idWF)}>
            <div className="bg-blue-400 font-medium text-sm text-white w-full h-full rounded-full grid place-items-center">
                <span className="rotate-[-20deg]">WF</span>
            </div>
        </TooltipEx>
    </div>
}

export const getWFTooltip = (causaleWF: PresenzeCausaleWFRecord | undefined, idWF: number) => {
    if (causaleWF) {
        let noteR = PresenzeCausaleWFRecord.getNoteRichiedente(causaleWF) || '';
        let noteA = PresenzeCausaleWFRecord.getNoteApprovatore(causaleWF) || '';

        const cGiornoDt = DateTime.fromISO(causaleWF.giornoDt!).toFormat('dd/LL/yyyy');
        let intervallo: string;
        if (causaleWF.giornataIntera) {
            intervallo = `per l'intera giornata`;
        } else {
            const entrata = causaleWF.oraInizio || '0000';
            const uscita = causaleWF.oraFine || '0000';
            intervallo = (entrata === '0000' && uscita === '0000') ? '?' : CoppiaTimbrature.formatHHMM(entrata) + ' - ' + CoppiaTimbrature.formatHHMM(uscita);
        }

        return <>id: #<span className="font-medium">{causaleWF.wf.idWF}</span><br/>
            Causale: <span className="font-medium">{PresenzeCausaleWFRecord.getDescrizioneCausale(causaleWF, true)}</span><br/>
            Data/orario rif.: <span className="font-medium">{cGiornoDt}&nbsp;<span className="text-xs">{intervallo}</span></span><br/>
            {noteR && <>Note richiedente: <span className="font-medium">{noteR}</span><br/></>}
            {noteA && <>Note approvatore: <span className="font-medium">{noteA}</span><br/></>}
        </>;
        // descrizioneCausale
        // oraFine
        // oraInizio
        // giornataIntera
    }
    return <span>#{idWF}</span>;
};

const MalattiaIcon: React.FC<{ idAttestato: string }> = ({idAttestato}) => {
    return <div className="bg-gray-200 mt-1 p-1 border-l rounded-l w-[38px] h-[38px]">
        <TooltipEx tooltip={`Num. attestato: #${idAttestato}`}>
            <div className="bg-red-600 font-medium text-sm text-white w-full h-full rounded-full grid place-items-center">
                <IconFirstAidKit className="ml-[0.03rem]"/>
            </div>
        </TooltipEx>
    </div>
}